import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import './styles.css';

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://api.ajouchong.com'
});

const WritePage = () => {
    const [form, setForm] = useState({ title: '', content: '' });
    const [message, setMessage] = useState({ success: '', error: '' });
    const navigate = useNavigate();
    const location = useLocation();

    // URL 경로로부터 타입 구분
    const getTypeFromPath = () => {
        if (location.pathname.includes('/qna/write')) return 'qna';
        if (location.pathname.includes('/require/write')) return 'require';
        return 'qna'; 
    };

    const type = getTypeFromPath();

    const getConfig = () => {
        switch (type) {
            case 'qna':
                return {
                    apiEndpoint: '/api/qna',
                    requestData: { qpTitle: form.title, qpContent: form.content },
                    title: 'QnA 작성',
                    redirectPath: '/communication/qna',                    
                };
            case 'require':
                return {
                    apiEndpoint: '/api/agora',
                    requestData: { apTitle: form.title, apContent: form.content },
                    title: '안건 상정 글 작성',
                    redirectPath: '/communication/require',
                };
            default:
                return {
                    apiEndpoint: '/api/qna',
                    requestData: { qpTitle: form.title, qpContent: form.content },
                    title: 'QnA 작성',
                    redirectPath: '/communication/qna',                    
                };
        }
    };

    const config = getConfig();

    const checkLoginStatus = useCallback(async () => {
        try {
            await apiClient.get("/api/login/auth/info", {
                withCredentials: true,
            });
        } catch {
            alert('로그인이 필요합니다.');
            navigate(config.redirectPath);
        }
    }, [navigate, config.redirectPath]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ success: '', error: '' });

        try {
            const response = await apiClient.post(
                config.apiEndpoint,
                config.requestData,
                { 
                    withCredentials: true, 
                    headers: { "Content-Type": "application/json" } 
                }
            );

            if (response.data.code === 1) {
                alert('게시글이 성공적으로 작성되었습니다.');
                navigate(config.redirectPath);
            } else {
                setMessage({ success: '', error: '게시글 작성에 실패했습니다.' });
            }
        } catch (error) {
            setMessage({
                success: '',
                error: error.response?.data?.message || '게시글 작성 중 오류가 발생했습니다.'
            });
        }
    };

    const handleBackToList = () => {
        navigate(config.redirectPath);
    };

    const renderMessage = () => (
        <>
            {message.success && <div className="write-message is-success" role="status">{message.success}</div>}
            {message.error && <div className="write-message is-error" role="alert">{message.error}</div>}
        </>
    );

    const renderForm = () => (
        <form onSubmit={handleSubmit} className="write-form">
            <div className="ui-field">
                <label htmlFor="title">제목</label>
                <input
                    type="text"
                    id="title"
                    name="title"
                    className="ui-input"
                    value={form.title}
                    onChange={handleChange}
                    required
                    placeholder="제목을 입력하세요"
                />
            </div>
            <div className="ui-field">
                <label htmlFor="content">내용</label>
                <textarea
                    id="content"
                    name="content"
                    className="ui-textarea write-content"
                    value={form.content}
                    onChange={handleChange}
                    required
                    placeholder="내용을 입력하세요"
                />
            </div>

            {renderMessage()}

            <div className="write-actions">
                <button type="button" onClick={handleBackToList} className="ui-btn">
                    <ArrowLeft size={18} aria-hidden="true" />
                    목록으로
                </button>
                <button type="submit" className="ui-btn is-primary write-submit">게시</button>
            </div>
        </form>
    );

    useEffect(() => {
        checkLoginStatus();
    }, [checkLoginStatus]);

    return (
        <div className="context">
            <div className="contextTitle">{config.title}</div>
            <hr className="titleSeparator" />

            {renderForm()}
        </div>
    );
};

export default WritePage;
